import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import { TASK_MODEL, TASK_SCHEDULE_MODEL } from '../../../services/entity/task';
import { DB_NULL } from '../../constants';
import { MEDIA_SERVICE } from '../../constants/media';
import { PATROL_EVIDENCE_DEFAULT_VALUES } from '../../constants/patrol';
import { SCHEDULE_PATTERNS } from '../../constants/schedule';
import { TASK_EVIDENCE_TYPE } from '../../constants/task';
import { allowedPatrolEvidence } from '../../helper/patrol';
import { getNewSiteData } from '../../helper/site';
import { getAssignee } from '../../helper/task';
import { omit } from '../../methods/helpers';
import { getTimeInSeconds } from '../../time';
import {
  transformDateToSelectedTimeZoneUTC,
  transformMultipleSchedule,
} from './schedule';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { getItemOrDefaultToValue } from 'utils/details';
import { hasService } from 'utils/helper/product';
import { serviceTypes } from 'utils/constants/serviceTypes';
import { PAGE_URLS } from 'utils/constants/index.js';
import { toJSON, fromJSON } from 'utils/helper/baseTasks';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(timezone);

const getSiteValue = ({
  regionId,
  regionName,
  companyId,
  companyName,
  businessClients,
  rawClients,
  sites,
  site,
  values: { newSite, ...data },
} = {}) => {
  if (newSite) {
    const { siteName, siteLocations, ...siteData } = getNewSiteData({
      newSite,
      regionId,
      regionName,
      companyId,
      companyName,
      businessClients,
      rawClients,
      values: { ...data, newSite },
    });
    return {
      siteData: { name: siteName, locations: siteLocations, ...siteData },
    };
  }

  if (site && site.businessClientId) {
    return { businessClientId: site.businessClientId, siteId: site.id };
  }

  if (typeof site === 'string') {
    const foundSite = sites?.find(item => item.id === site);
    if (foundSite) {
      return {
        businessClientId: foundSite.businessClientId,
        siteId: foundSite.id,
      };
    }
  }
  if (typeof site === 'object') {
    const foundSite = sites?.find(item => item.id === site?.id);
    if (foundSite) {
      return {
        businessClientId: foundSite.businessClientId,
        siteId: foundSite.id,
      };
    }
  }

  if (data.site && data.businessClient) {
    return { businessClientId: data.businessClient, siteId: data.site };
  }

  if (data.site) {
    const foundSite = sites.find(item => item.id === data.site.id);
    if (foundSite) {
      return {
        businessClientId: foundSite.businessClientId,
        siteId: foundSite.id,
      };
    }
  }
  return {
    businessClientId: data.businessClientId,
    siteId: data.siteId ? data.siteId : DB_NULL,
  };
};

export const formatOptions = data => {
  if (!data) return '';
  const format = { options: data };
  return format;
};

export const transformEvidenceForm = ({ data }) => {
  return {
    [TASK_MODEL.evidence.id]: data.map(item => {
      return {
        id: item.id || uuidv4(),
        ...(item.others && { ...item.others.value }),
        [TASK_MODEL.evidence.type.id]: item.type.value,
        [TASK_MODEL.evidence.description.id]: item.description.value,
        ...((item.type.value === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id ||
          item.type.value === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id) &&
          item.textOptions?.value && {
            contents: toJSON(formatOptions(item.textOptions.value)),
          }),
      };
    }),
  };
};
export const transformCollectedEvidenceForm = ({ data }) => {
  return {
    [TASK_MODEL.evidence.id]: data.map(item => {
      return {
        id: item.id || uuidv4(),
        ...(item.others && { ...item.others.value }),
        [TASK_MODEL.evidence.type.id]: item.type,
        [TASK_MODEL.evidence.description.id]: item.description,
        ...((item.type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id ||
          item.type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id) &&
          item.contents?.value && {
            contents: toJSON(item.contents?.value),
          }),
        ...(item.type === TASK_EVIDENCE_TYPE.CHECKBOX.id && {
          contents: Array.isArray(item.contents.value),
        }),
        ...((item.type === TASK_EVIDENCE_TYPE.SIGNATURE.id ||
          item.type === TASK_EVIDENCE_TYPE.IMAGE.id ||
          item.type === TASK_EVIDENCE_TYPE.AUDIO.id ||
          item.type === TASK_EVIDENCE_TYPE.DESCRIPTION.id ||
          item.type === TASK_EVIDENCE_TYPE.NUMERIC.id) && {
          contents: item.contents.value,
        }),

        completed: item.completed.value,
      };
    }),
  };
};

export const transformEvidenceInitialValue = ({ evidence }) => {
  return (evidence || []).map(({ type, description, ...item } = {}) => {
    if (
      type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id ||
      type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id
    ) {
      const options = item.contents && fromJSON(item.contents)?.options;
      return {
        type: {
          value: type,
          displayValue: TASK_EVIDENCE_TYPE[type]?.name,
          type: 'text',
        },
        description: { value: description, type: 'text' },
        others: { display: 'none', value: item },
        textOptions: {
          display: undefined,
          displayValue: '',
          doNotShow: false,
          value: options,
          type: 'dynamic-text-generator',
        },
      };
    }
    return {
      type: {
        value: type,
        displayValue: TASK_EVIDENCE_TYPE[type]?.name,
        type: 'text',
      },
      description: { value: description, type: 'text' },
      others: { display: 'none', value: item },
      textOptions: {
        display: undefined,
        displayValue: '',
        doNotShow: true,
        value: null,
        type: 'dynamic-text-generator',
      },
    };
  });
};

export const getSchedulePatternValues = ({
  schedule,
  pattern,
  assigneeId,
  timezone,
}) => {
  const baseSchedule = {
    [TASK_SCHEDULE_MODEL.id.id]: schedule.id || uuidv4(),
    [TASK_SCHEDULE_MODEL.startAfter.id]: schedule[pattern].startAfter,
    [TASK_SCHEDULE_MODEL.finishBefore.id]: schedule[pattern].finishBefore,
    [TASK_SCHEDULE_MODEL.assignToUserIDs.id]: assigneeId ? [assigneeId] : null,
    timezone,
  };

  switch (pattern) {
    case SCHEDULE_PATTERNS.simpleInterval.label:
      return {
        [SCHEDULE_PATTERNS.simpleInterval.id]: {
          ...baseSchedule,
          [TASK_SCHEDULE_MODEL.intervalInMinutes.id]:
            schedule[pattern].interval,
        },
      };
    case SCHEDULE_PATTERNS.weekly.label:
      return {
        [SCHEDULE_PATTERNS.weekly.id]: {
          ...baseSchedule,
          [TASK_SCHEDULE_MODEL.intervalInMinutes.id]:
            schedule[pattern].interval,
          [TASK_SCHEDULE_MODEL.daysOfWeek.id]: schedule[pattern].days,
        },
      };
    default:
      return { [SCHEDULE_PATTERNS.onceOff.id]: { ...baseSchedule } };
  }
};

export const transformTaskSchedule = ({ schedule, addSchedule }) => {
  //If addSchedule toggle is off, then do not add the schedule even if there was start time and finsih time added
  if (!addSchedule) {
    return { schedule: null };
  }
  if (!schedule) return { schedule: null };
  if (!addSchedule && isEmpty(schedule.onceOff)) return { schedule };

  const selectedTimeZone = schedule.onceOff.timezone;
  // convert the start time and finish time to utc using the timezone provided
  const convertedFinishBefore = schedule.onceOff.finishBefore
    ? transformDateToSelectedTimeZoneUTC(
        schedule.onceOff.finishBefore,
        selectedTimeZone, // selected
      )
    : DB_NULL;
  const convertedStartAfter = transformDateToSelectedTimeZoneUTC(
    schedule.onceOff.startAfter,
    selectedTimeZone, // selected
  );

  return {
    schedule: {
      [TASK_SCHEDULE_MODEL.id.id]: schedule.onceOff.id || uuidv4(),
      [TASK_SCHEDULE_MODEL.startAfter.id]: convertedStartAfter,
      [TASK_SCHEDULE_MODEL.finishBefore.id]: convertedFinishBefore,
    },
  };
};

export const getFormSiteValue = ({
  regionId,
  regionName,
  companyId,
  companyName,
  businessClients,
  rawClients,
  values: { newSite, ...data },
  site,
} = {}) => {
  if (newSite) {
    const { siteName, siteLocations, ...siteData } = getNewSiteData({
      newSite,
      regionId,
      regionName,
      companyId,
      companyName,
      businessClients,
      rawClients,
      values: { ...data, newSite },
    });
    return {
      siteData: { name: siteName, locations: siteLocations, ...siteData },
    };
  }

  if (site) {
    return {
      businessClientId: site.businessClientId,
      siteId: site.siteId,
    };
  }
  // need to change this after changes in initial values
  return { businessClientId: data.businessClientId, siteId: data.siteId };
};

export const transformTaskFormValues = ({
  values,
  additionalMarkers,
  task,
  id,
  regionId,
  regionName,
  companyId,
  companyName,
  businessClients,
  rawClients,
  sites,
  timezone,
  multiSchedule,
  isSubtask,
  users,
  mobileStatus,
  timeStarted,
  startedByID,
  startedByFullName,
  timeFinished,
  finishedByID,
  finishedByFullName,
}) => {
  const {
    taskDescription,
    taskName,
    taskType,
    site,
    assetId,
    severityRating,
    severity,
    ...data
  } = values;
  const {
    mediaService = MEDIA_SERVICE.TASK,
    evidence = [],
    checkList = [],
    location,
    isTemplate,
    wrapsId,
    scanCode,
    scanAutoStart,
    scanStartOnly,
    surfaceViewPreferred,
    status,
    taskImage,
    assigneeId,
    subtasks,
    deletedsubtasks = [],
    subtasksSequentialOrder,
    templateId,
    templateValues = {},
    schedule,
    addSchedule,
    archived,
    schedulePatterns,
    deletedschedulePatterns = [],
    expectedDuration,
    siteSurfaceImages,
    ...rest
  } = data;

  const taskId = data.id || uuidv4();
  // disabled for now as it will be a feature when we can toggle subtasks
  // const joins = isEmpty(task) ? [] : task.joins;
  const joins = [];
  const surfaces = isEmpty(task) ? [] : task.surfaces;
  const currentTask = data.id ? task.tasks.find(item => item.id === id) : {};
  const ids = values?.departments &&
    !isEmpty(values?.departments) && {
      departmentIds: getItemOrDefaultToValue(
        values,
        'departments',
        undefined,
      )?.map(m => m.id),
    };
  const { siteId, businessClientId } = getSiteValue({
    values: { ...rest },
    regionId,
    regionName,
    companyId,
    companyName,
    businessClients,
    rawClients,
    sites,
    site,
  });

  const tasks = [
    {
      ...omit(currentTask, [
        'businessClient',
        'site',
        'departmentIds',
        'departments',
        'severity',
      ]),
      ...templateValues,
      archived,
      [TASK_MODEL.id.id]: taskId,
      ...(!isEmpty(severity) && { [TASK_MODEL.severity.id]: severity }),
      [TASK_MODEL.severityRating.id]: severityRating,
      [TASK_MODEL.taskName.id]: taskName,
      [TASK_MODEL.description.id]: taskDescription,
      [TASK_MODEL.isTemplate.id]: isTemplate,
      [TASK_MODEL.wrapsId.id]: wrapsId,
      [TASK_MODEL.scanCode.id]: scanCode,
      [TASK_MODEL.scanAutoStart.id]: scanAutoStart,
      [TASK_MODEL.scanStartOnly.id]: scanStartOnly,
      [TASK_MODEL.surfaceViewPreferred.id]: surfaceViewPreferred,
      [TASK_MODEL.status.id]: mobileStatus || status,
      [TASK_MODEL.timeStarted.id]: timeStarted,
      [TASK_MODEL.startedByID.id]: startedByID,
      [TASK_MODEL.startedByName.id]: startedByFullName,
      [TASK_MODEL.timeEnded.id]: timeFinished,
      [TASK_MODEL.finishedByID.id]: finishedByID,
      [TASK_MODEL.finishedByName.id]: finishedByFullName,
      subtasksSequentialOrder,
      ...transformEvidenceForm({ data: [...evidence, ...checkList] }),
      // [TASK_MODEL.taskType.id]: taskType,
      [TASK_MODEL.taskTypeID.id]: taskType?.id,

      ...(taskImage && {
        media: { image: taskImage, service: mediaService },
      }),
      ...(expectedDuration && {
        [TASK_MODEL.expectedDuration.id]: getTimeInSeconds(expectedDuration),
      }),
      ...getAssignee({ assigneeId, users }),
      [TASK_MODEL.surfaceU.id]: isEmpty(location) ? DB_NULL : location[0].lng,
      [TASK_MODEL.surfaceV.id]: isEmpty(location) ? DB_NULL : location[0].lat,
      templateId: templateId && templateId.id ? templateId.id : templateId,
      ...getSiteValue({
        values: { ...rest },
        regionId,
        regionName,
        companyId,
        companyName,
        businessClients,
        rawClients,
        sites,
        site,
      }),
      ...(multiSchedule &&
        !isSubtask &&
        Array.isArray(schedulePatterns) && {
          schedulePatterns: transformMultipleSchedule({
            values: schedulePatterns.concat(
              deletedschedulePatterns.filter(i => Boolean(i.id)),
            ),
            regionId,
            regionName,
            companyId,
            companyName,
            businessClients,
            rawClients,
            sites,
            timezone,
          }),
        }),
      ...(!multiSchedule &&
        !isSubtask && {
          ...transformTaskSchedule({
            schedule,
            addSchedule,
            timezone,
          }),
        }),
      [TASK_MODEL.surfaceId.id]:
        siteSurfaceImages.id === GEOGRAPHICAL_MAP.id
          ? DB_NULL
          : siteSurfaceImages.id,
      assetId: assetId ? assetId.id : DB_NULL,
      departmentIds: ids ? ids.departmentIds : [],
    },
  ];
  // disabled for now as it will be a feature when we can toggle subtasks
  if (false) {
    const deleted = isEmpty(task) ? [] : deletedsubtasks;

    const deletedSubtask = deleted.map(item => {
      // archive subtasks of parent
      const archiveSubtasks = (value = []) => {
        value.forEach(st => {
          if (st.subtasks) {
            const subtasksToArchive = archiveSubtasks(st.subtasks);
            st.subtasks = subtasksToArchive;
          }
        });
        return value.map(k => ({ ...k, archived: true }));
      };

      return {
        ...item,
        archived: true,
        subtasks: archiveSubtasks(item.subtasks),
      };
    });

    subtasks.concat(deletedSubtask).forEach((item, index) => {
      const findIndex = joins.findIndex(join => join.subtaskId === item.id);

      if (findIndex < 0) {
        joins.push({
          id: uuidv4(),
          subtaskId: item.id,
          taskId,
          templateId: item.joinTemplateId || item.templateId,
          surfaceId:
            item.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id
              ? DB_NULL
              : item.siteSurfaceImages.id,
          sequence: index + 1,
          surfaceU:
            item?.location && item.location.length > 0
              ? item.location[0].lng
              : DB_NULL,
          surfaceV:
            item?.location && item.location.length > 0
              ? item.location[0].lat
              : DB_NULL,
        });
      } else {
        // match additional markers id with sub task location id
        const updatedLocation = (additionalMarkers || []).find(
          j => !isEmpty(item.location) && j.id === item.location[0].id,
        );
        const indX = subtasks.findIndex(s => s.id === item.id);
        joins[findIndex] = {
          ...joins[findIndex],
          subtaskId: item.id,
          taskId,
          templateId: item.joinTemplateId || item.templateId,
          surfaceId:
            item.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id
              ? DB_NULL
              : item.siteSurfaceImages.id,
          surfaceU: updatedLocation ? updatedLocation.lng : DB_NULL,
          surfaceV: updatedLocation ? updatedLocation.lat : DB_NULL,
          sequence: updatedLocation ? updatedLocation.seqNumber : indX + 1,
        };
      }
      const { tasks: subTasks } = transformTaskFormValues({
        values: { ...item, siteId, businessClientId },
        task: {
          tasks: item.deletedsubtasks ? (task ? task.tasks : tasks) : tasks,
          joins,
          surfaces,
        },
        regionId,
        regionName,
        companyId,
        companyName,
        businessClients,
        rawClients,
        sites,
        users,
        isSubtask: true,
      });

      tasks.push(...subTasks);
    });
  }

  if (isTemplate && assigneeId && !addSchedule) {
    const cloneTask = transformTaskFormValues({
      values: { ...values, isTemplate: false },
      task,
      id,
      regionId,
      regionName,
      companyId,
      companyName,
      businessClients,
      rawClients,
      sites,
      timezone,
      users,
    });

    // joins.push(...cloneTask.joins);
    tasks.push(...cloneTask.tasks);
  }
  return { joins, tasks, surfaces };
};

export const transformPatrolTaskEvidence = ({
  evidence,
  location,
  businessClient,
  site,
  taskName,
}) => {
  if (!evidence || !location) return null;

  const evidenceArray = Object.values(evidence);

  if (!location.text || !allowedPatrolEvidence.includes(location.type)) {
    return null;
  }

  const evidenceData = evidenceArray.find(
    item => item.seqNumber === location.seqNumber,
  );

  if (evidenceData && evidenceData.value.length > 0) {
    const transformedEvidence = transformEvidenceForm({
      data: evidenceData.value,
    });

    return {
      ...PATROL_EVIDENCE_DEFAULT_VALUES,
      id: location.taskId || uuidv4(),
      name: `${taskName} Checkpoint ${location.text} Tasks`,
      description: `Evidence for ${taskName} Checkpoint ${location.text} Tasks`,
      businessClientId: businessClient ? businessClient.id : null,
      siteId: site ? site.id : null,
      ...transformedEvidence,
    };
  }

  return null;
};

export const transformDefaultSubTask = ({
  defaultValues = {},
  values,
  editModeValues,
  surfaceImageId,
}) => {
  if (editModeValues?.additionalMarkers) {
    if (surfaceImageId === GEOGRAPHICAL_MAP.id) {
      editModeValues.additionalMarkers = editModeValues.additionalMarkers.filter(
        marker => marker.surfaceUuid === GEOGRAPHICAL_MAP.id,
      );
    } else {
      editModeValues.additionalMarkers = editModeValues.additionalMarkers
        ? editModeValues.additionalMarkers.filter(
            marker =>
              marker.surfaceUuid === editModeValues.siteSurfaceImages.id,
          )
        : [];
    }
  }

  return {
    ...defaultValues,
    site: editModeValues?.site ? editModeValues.site : values.site,
    siteSurfaceImages: editModeValues?.siteSurfaceImages
      ? editModeValues?.siteSurfaceImages
      : values.siteSurfaceImages,
  };
};

export const transformTaskTypeList = list => {
  return list.map(item => ({
    id: item.id,
    name: item.typeName,
    category: item.category,
    categoryIcon: item.categoryIcon,
    typeName: item.typeName,
  }));
};

export const taskCategoriesBasedOnView = ({ view, services }) => {
  const taskCategories = [];

  const hasCleaningServices = hasService({
    // if cleaning service is subscribed, then return cleaning filters
    service: serviceTypes.cleaning,
    services,
  });
  const hasMaintenanceServices = hasService({
    // if maintenance service is subscribed, then return cleaning filters
    service: serviceTypes.maintenance,
    services,
  });
  const hasSecurityTask = hasService({
    // if CheckPointTask service is subscribed
    service: serviceTypes.security,
    services,
  });

  switch (view) {
    case PAGE_URLS.TEMPLATE_TASKS.link:
    case PAGE_URLS.ASSETS.link:
    case PAGE_URLS.SITES.link:
    case PAGE_URLS.EVENTS.link: {
      if (hasCleaningServices) {
        taskCategories.push(serviceTypes.cleaning);
      }
      if (hasMaintenanceServices) {
        taskCategories.push(serviceTypes.maintenance);
      }
      if (hasSecurityTask) {
        taskCategories.push(serviceTypes.security);
      }

      return taskCategories;
    }

    case PAGE_URLS.CLEANING_TASKS.link: {
      return [serviceTypes.cleaning];
    }
    case PAGE_URLS.WORK_ORDERS.link: {
      return [serviceTypes.maintenance];
    }
    case PAGE_URLS.SECURITY_TASKS.link: {
      return [serviceTypes.security];
    }
    default:
      return '';
  }
};
